import { z } from "zod";
const id = z.string().trim().min(1).max(100);
const key = z.string().regex(/^[a-z][a-z0-9_-]{0,49}$/);
export const processFieldSchema = z
    .object({
    key,
    label: z.string().trim().min(2).max(160),
    type: z.enum(["text", "textarea", "multiselect"]),
    required: z.boolean().default(true),
    options: z.array(z.string().trim().min(1).max(120)).max(30).default([])
})
    .refine((field) => field.type !== "multiselect" || field.options.length > 0, "Informe as opções do campo.");
export const processStepDefinitionSchema = z.object({
    key,
    title: z.string().trim().min(3).max(160),
    description: z.string().trim().max(4000).default(""),
    teamHint: z.string().trim().max(100).default(""),
    assigneeHint: z.string().trim().max(100).default(""),
    durationDays: z.number().int().min(1).max(365),
    dependsOn: z.array(key).max(50).default([]),
    checklist: z.array(z.string().trim().min(2).max(250)).max(30).default([]),
    fields: z.array(processFieldSchema).max(20).default([]),
    requireEvidence: z.boolean().default(true)
});
export const processTemplateDefinitionSchema = z
    .object({
    name: z.string().trim().min(3).max(160),
    description: z.string().trim().max(2000).default(""),
    steps: z.array(processStepDefinitionSchema).min(1).max(50)
})
    .superRefine((definition, context) => {
    const keys = new Set(definition.steps.map((step) => step.key));
    if (keys.size !== definition.steps.length)
        context.addIssue({
            code: "custom",
            message: "As etapas precisam de identificadores únicos."
        });
    for (const step of definition.steps) {
        if (new Set(step.fields.map((field) => field.key)).size !==
            step.fields.length)
            context.addIssue({
                code: "custom",
                message: `Campos repetidos em ${step.title}.`
            });
        if (new Set(step.dependsOn).size !== step.dependsOn.length ||
            step.dependsOn.some((dependency) => !keys.has(dependency) || dependency === step.key))
            context.addIssue({
                code: "custom",
                message: `Dependência inválida em ${step.title}.`
            });
    }
    const pending = new Set(keys);
    for (let round = 0; round < definition.steps.length; round++) {
        for (const step of definition.steps)
            if (step.dependsOn.every((dependency) => !pending.has(dependency)))
                pending.delete(step.key);
    }
    if (pending.size)
        context.addIssue({
            code: "custom",
            message: "As dependências formam um ciclo. Revise a ordem das etapas."
        });
});
export const processListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).max(100000).default(1),
    status: z.enum(["ACTIVE", "COMPLETED", "CANCELLED", ""]).default("ACTIVE"),
    search: z.string().trim().max(180).default(""),
    scope: z.enum(["all", "mine"]).default("all")
});
export const processVersionSchema = z.object({
    expectedVersion: z.number().int().min(1)
});
export const saveProcessTemplateSchema = processTemplateDefinitionSchema.safeExtend({
    expectedVersion: z.number().int().min(1).optional()
});
export const processTeamSchema = z.object({
    name: z.string().trim().min(2).max(100),
    description: z.string().trim().max(1000).default(""),
    memberIds: z
        .array(id)
        .max(100)
        .refine((items) => new Set(items).size === items.length, "Usuário repetido."),
    isActive: z.boolean().default(true),
    expectedVersion: z.number().int().min(1).optional()
});
export const createProcessSchema = z.object({
    templateId: id,
    title: z.string().trim().min(3).max(180),
    clientName: z.string().trim().min(2).max(160),
    companyId: id.nullable().optional(),
    priority: z.enum(["NORMAL", "HIGH", "URGENT"]).default("NORMAL"),
    assignments: z
        .array(z.object({ key, teamId: id.nullable(), assignedUserId: id.nullable() }))
        .min(1)
        .max(50),
    idempotencyKey: z.string().uuid()
});
export const updateProcessStepSchema = processVersionSchema.extend({
    action: z.enum(["START", "SAVE", "BLOCK", "RESUME", "COMPLETE", "REASSIGN"]),
    notes: z.string().trim().max(12000).default(""),
    values: z
        .record(key, z.union([
        z.string().trim().max(12000),
        z.array(z.string().trim().max(120)).max(30)
    ]))
        .default({}),
    checkedItems: z.array(z.number().int().min(0).max(29)).max(30).default([]),
    reason: z.string().trim().max(2000).default(""),
    teamId: id.nullable().optional(),
    assignedUserId: id.nullable().optional(),
    dueAt: z.string().datetime({ offset: true }).nullable().optional()
});
export const cancelProcessSchema = processVersionSchema.extend({
    reason: z.string().trim().min(5).max(2000)
});
export function processCompletionErrors(definition, values, checkedItems, notes) {
    const errors = [];
    if (checkedItems.some((item) => !Number.isInteger(item) ||
        item < 0 ||
        item >= definition.checklist.length))
        errors.push("Item de checklist inválido.");
    if (definition.checklist.some((_, index) => !checkedItems.includes(index)))
        errors.push("Conclua todos os itens obrigatórios do checklist.");
    if (definition.requireEvidence && notes.trim().length < 10)
        errors.push("Registre o resultado da etapa com pelo menos 10 caracteres.");
    for (const field of definition.fields) {
        const value = values[field.key];
        if (field.required &&
            (!value || (Array.isArray(value) ? value.length === 0 : !value.trim())))
            errors.push(`Preencha: ${field.label}.`);
        if (value !== undefined &&
            (field.type === "multiselect"
                ? !Array.isArray(value) ||
                    value.some((option) => !field.options.includes(option))
                : typeof value !== "string"))
            errors.push(`Valor inválido: ${field.label}.`);
    }
    return errors;
}
export function processStepAttention(step, now = new Date()) {
    if (step.status === "COMPLETED")
        return null;
    if (step.status === "BLOCKED")
        return "blocked";
    if (!step.assignedUserId && !step.teamId)
        return "unassigned";
    if (!step.availableAt || !step.dueAt)
        return null;
    const remaining = new Date(step.dueAt).getTime() - now.getTime();
    return remaining < 0 ? "overdue" : remaining <= 86400000 ? "soon" : null;
}
const field = (key, label, type = "textarea", options = []) => ({ key, label, type, required: true, options });
const step = (key, title, teamHint, durationDays, dependsOn, description, checklist, fields, assigneeHint = "") => ({
    key,
    title,
    teamHint,
    durationDays,
    dependsOn,
    description,
    checklist,
    fields,
    assigneeHint,
    requireEvidence: true
});
export const IT_ONBOARDING_TEMPLATE = {
    name: "Implantação de cliente • TI gerenciada",
    description: "Da coleta cadastral à orientação do cliente, com aprovações, levantamento técnico e implantação acompanhados etapa por etapa.",
    steps: [
        step("cadastro", "Coletar os dados da empresa", "Administrativo", 2, [], "Confirme os dados cadastrais e os contatos autorizados antes de iniciar o atendimento.", [
            "Dados conferidos com o cliente",
            "Responsável e contatos autorizados identificados"
        ], [
            field("razao_social", "Razão social", "text"),
            field("cnpj", "CNPJ", "text"),
            field("endereco", "Endereço e unidades"),
            field("contatos", "Responsáveis, telefones e e-mails")
        ]),
        step("escopo", "Definir o escopo do atendimento", "Direção", 2, ["cadastro"], "Registre os serviços contratados, limites e exclusões. A implantação depende dessa decisão.", ["Escopo aprovado pela Direção", "Limites e exclusões registrados"], [
            field("servicos", "Serviços incluídos", "multiselect", [
                "Rede",
                "Computadores",
                "Servidores",
                "Antivírus",
                "Site",
                "Hospedagem de e-mail",
                "Backup",
                "Outros"
            ]),
            field("limites", "Limites, exclusões e condições")
        ]),
        step("avaliacao", "Avaliar a rede e os computadores no local", "", 3, ["escopo"], "Felipe realiza a visita técnica e avalia rede, computadores, servidores e riscos do ambiente.", [
            "Visita técnica realizada",
            "Rede e computadores avaliados",
            "Riscos e problemas identificados"
        ], [field("levantamento", "Levantamento técnico e diagnóstico")], "Felipe"),
        step("melhorias", "Apresentar as melhorias à Direção", "", 1, ["avaliacao"], "Encaminhe o diagnóstico e as recomendações, com prioridade e impacto de cada melhoria.", ["Relatório apresentado à Direção", "Prioridades e riscos explicados"], [field("recomendacoes", "Melhorias recomendadas e prioridades")], "Felipe"),
        step("decisao", "Aprovar e executar o plano de ação", "Direção", 3, ["melhorias"], "A Direção decide o que será feito, registra responsáveis e providencia as ações aprovadas.", [
            "Decisão registrada para cada recomendação",
            "Ações aprovadas executadas ou encaminhadas com prazo"
        ], [field("decisoes", "Decisões, responsáveis e ações adotadas")]),
        step("acessos", "Regularizar os acessos aos equipamentos", "Externo", 2, ["decisao"], "Felipe e Externo realizam a visita, redefinem as credenciais autorizadas e validam os acessos. Use o cofre de senhas da empresa; registre aqui apenas a referência, nunca a senha.", [
            "Felipe e Externo participaram da intervenção",
            "Credenciais autorizadas redefinidas",
            "Acessos testados e guardados no cofre"
        ], [field("equipamentos", "Equipamentos gerenciados e referência no cofre")], "Felipe"),
        step("ninja", "Instalar o Ninja RMM nas máquinas", "Externo", 2, ["acessos"], "Instale o agente em todos os computadores previstos no escopo e confirme a comunicação no console.", [
            "Agentes instalados nas máquinas previstas",
            "Máquinas online e vinculadas ao cliente no Ninja",
            "Exceções justificadas no resultado"
        ], [field("inventario", "Máquinas instaladas, validação e exceções")]),
        step("impressoras", "Mapear impressoras e endereços IP", "Externo", 2, ["acessos"], "Identifique as impressoras, endereços IP, localização e forma de conexão.", ["Todas as impressoras inventariadas", "IPs e localização documentados"], [field("mapa", "Inventário de impressoras, IPs e localização")]),
        step("orientacao", "Ensinar o cliente a abrir chamados", "Externo", 1, ["ninja", "impressoras"], "Oriente os contatos do cliente sobre abertura de chamados, canais oficiais e informações necessárias. Confirme que conseguem entrar em contato com a Help Tech.", [
            "Canais de atendimento apresentados",
            "Abertura de chamado demonstrada",
            "Cliente confirmou o recebimento das orientações"
        ], [field("treinamento", "Pessoas orientadas e confirmação do cliente")])
    ]
};
